import axios from 'axios';

const isLocalhost = typeof window !== 'undefined'
    && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const rawBaseURL = process.env.REACT_APP_API_URL
    || (isLocalhost ? 'http://localhost:5000/api' : 'https://237elecsolution.com/api');
// Strip trailing slashes and collapse any accidental double slashes (e.g. a misconfigured env var)
const baseURL = rawBaseURL.replace(/\/+$/, '').replace(/([^:])\/{2,}/g, '$1/');

const API = axios.create({ baseURL });

// Attach token to every request if available
API.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Auth
export const registerUser = (data) => API.post('/auth/register', data);
export const loginUser = (data) => API.post('/auth/login', data);
export const getMe = () => API.get('/auth/me');
export const changePassword = (data) => API.put('/auth/password', data);

// File uploads
// Origin of the API (without the trailing /api) so uploaded files resolve
// correctly whether the backend is on a separate dev port or the same prod host.
export const API_ORIGIN = baseURL.replace(/\/api$/, '');
export const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await API.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    const url = res.data?.url || '';
    return url.startsWith('/') ? `${API_ORIGIN}${url}` : url;
};

// Products
export const getProducts = () => API.get('/products');
export const getProductsByCategory = (category) => API.get(`/products/category/${category}`);
export const getProduct = (id) => API.get(`/products/${id}`);
export const getCategories = () => API.get('/products/info/categories');
export const getProductImages = (id) => API.get(`/products/${id}/images`);

// Courses
export const getCourses = () => API.get('/courses');
export const getCoursesByType = (type) => API.get(`/courses/type/${type}`);

// Warehouses
export const getWarehouses = () => API.get('/warehouses');

// Transit
export const getTransitServices = () => API.get('/transit');

// Contact
export const submitContact = (data) => API.post('/contact', data);
export const subscribe = (email) => API.post('/contact/subscribe', { email });

// Orders
export const createOrder = (data) => API.post('/orders', data);

// Admin
export const getAdminStats = () => API.get('/admin/stats');
export const getAdminOrders = () => API.get('/admin/orders');
export const updateOrderStatus = (id, status) => API.put(`/admin/orders/${id}/status`, { status });
export const getAdminUsers = () => API.get('/admin/users');
export const createAdminUser = (data) => API.post('/admin/users', data);
export const getAdminMessages = () => API.get('/admin/messages');
export const deleteAdminMessage = (id) => API.delete(`/admin/messages/${id}`);
export const createProduct = (data) => API.post('/admin/products', data);
export const updateProduct = (id, data) => API.put(`/admin/products/${id}`, data);
export const deleteProduct = (id) => API.delete(`/admin/products/${id}`);
export const bulkDeleteProducts = (ids) => API.post('/admin/products/bulk-delete', { ids });
export const addProductImage = (productId, data) => API.post(`/admin/products/${productId}/images`, data);
export const deleteProductImage = (id) => API.delete(`/admin/product-images/${id}`);
export const createCourse = (data) => API.post('/admin/courses', data);
export const updateCourse = (id, data) => API.put(`/admin/courses/${id}`, data);
export const deleteCourse = (id) => API.delete(`/admin/courses/${id}`);
export const bulkDeleteCourses = (ids) => API.post('/admin/courses/bulk-delete', { ids });

// Admin - Inventory
export const getInventoryOverview = () => API.get('/admin/inventory/overview');
export const getDailyInventory = (date) => API.get('/admin/inventory/daily', { params: date ? { date } : {} });
export const getMonthlyInventory = (year, month) => API.get('/admin/inventory/monthly', { params: { year, month } });
export const adjustStock = (data) => API.post('/admin/inventory/adjust', data);
export const getCompleteInventory = () => API.get('/admin/inventory/complete');

// Admin - Provider Goods
export const getAdminProviderGoods = () => API.get('/admin/provider-goods');
export const approveProviderGood = (id, data) => API.put(`/admin/provider-goods/${id}/approve`, data);
export const rejectProviderGood = (id, data) => API.put(`/admin/provider-goods/${id}/reject`, data);

// Reviews (public + authenticated)
export const getProductReviews = (productId) => API.get(`/reviews/product/${productId}`);
export const getReviewsSummary = () => API.get('/reviews/summary');
export const getReviewEligibility = (productId) => API.get(`/reviews/eligibility/${productId}`);
export const createReview = (data) => API.post('/reviews', data);
export const deleteReview = (id) => API.delete(`/reviews/${id}`);

// Admin - Reviews
export const getAdminReviews = () => API.get('/admin/reviews');
export const setReviewVisibility = (id, isHidden) => API.put(`/admin/reviews/${id}/visibility`, { is_hidden: isHidden });
export const deleteAdminReview = (id) => API.delete(`/admin/reviews/${id}`);

// Provider
export const getProviderGoods = () => API.get('/provider/goods');
export const createProviderGood = (data) => API.post('/provider/goods', data);
export const updateProviderGood = (id, data) => API.put(`/provider/goods/${id}`, data);
export const deleteProviderGood = (id) => API.delete(`/provider/goods/${id}`);
export const getProviderStats = () => API.get('/provider/stats');

export default API;
